import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { adminApi, errMsg } from "../api";
import { COLLECTIONS } from "../config";
import { Field, TextInput, TextArea, Toggle, StringListEditor, ObjectListEditor, UploadButton, SaveBar } from "../components";
import { toast } from "../../components/ui/sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function CollectionEditor() {
  const { collection, id } = useParams();
  const cfg = COLLECTIONS[collection];
  const isNew = id === "new";
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isNew) {
      const blank = {};
      cfg.fields.forEach((f) => {
        blank[f.key] = f.default ?? (f.type === "stringlist" || f.type === "objlist" ? [] : f.type === "toggle" ? false : f.type === "number" ? 0 : "");
      });
      setDoc(blank);
    } else {
      adminApi.get(cfg.api).then((r) => {
        const found = r.data.find((d) => d.id === id);
        if (found) setDoc(found); else toast.error("Item not found");
      }).catch((e) => toast.error(errMsg(e)));
    }
    // eslint-disable-next-line
  }, [collection, id]);

  if (!cfg) return <div className="adm-empty">Unknown collection</div>;
  if (!doc) return <div className="adm-empty">Loading…</div>;

  const set = (key, v) => setDoc((d) => ({ ...d, [key]: v }));

  const save = async () => {
    setSaving(true);
    try {
      if (isNew) {
        const { data } = await adminApi.post(cfg.api, doc);
        toast.success(`${cfg.singular} created`);
        navigate(`/admin/${collection}/${data.id}`, { replace: true });
      } else {
        await adminApi.put(`${cfg.api}/${id}`, doc);
        toast.success("Saved — live on the public site");
      }
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  const renderField = (f) => {
    const v = doc[f.key];
    switch (f.type) {
      case "textarea": return <TextArea value={v} onChange={(x) => set(f.key, x)} rows={f.rows || 4} data-testid={`field-${f.key}`} />;
      case "number": return <TextInput type="number" value={v} onChange={(x) => set(f.key, Number(x))} data-testid={`field-${f.key}`} />;
      case "date": return <TextInput type="date" value={v} onChange={(x) => set(f.key, x)} data-testid={`field-${f.key}`} />;
      case "toggle": return <Toggle checked={v} onChange={(x) => set(f.key, x)} label={v ? "On" : "Off"} testid={`field-${f.key}`} />;
      case "select":
        return (
          <select value={v ?? ""} onChange={(e) => set(f.key, e.target.value)} data-testid={`field-${f.key}`}>
            <option value="">—</option>
            {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        );
      case "stringlist": return <StringListEditor items={v || []} onChange={(x) => set(f.key, x)} testid={`field-${f.key}`} />;
      case "objlist": return <ObjectListEditor items={v || []} onChange={(x) => set(f.key, x)} fields={f.itemFields} testid={`field-${f.key}`} />;
      case "media":
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            {v && <img className="thumb" src={`${BACKEND_URL}/api/media/${v}`} alt="" style={{ width: 64, height: 64 }} />}
            <UploadButton onUploaded={(m) => set(f.key, m.id)} kind={collection} testid={`upload-${f.key}`} />
            {v && <button type="button" className="adm-btn danger sm" onClick={() => set(f.key, "")}>Remove</button>}
          </div>
        );
      default: return <TextInput value={v} onChange={(x) => set(f.key, x)} data-testid={`field-${f.key}`} />;
    }
  };

  return (
    <div data-testid={`admin-${collection}-editor`}>
      <div className="adm-head">
        <div>
          <h1>{isNew ? `New ${cfg.singular}` : `Edit: ${doc.title || doc.question || cfg.singular}`}</h1>
          <div className="sub"><Link to={`/admin/${collection}`}>← Back to {cfg.title}</Link></div>
        </div>
        <SaveBar onSave={save} saving={saving} testid={`save-${collection}`} />
      </div>
      <div className="adm-card">
        {cfg.fields.map((f) => (
          <Field key={f.key} label={f.label} hint={f.hint}>{renderField(f)}</Field>
        ))}
        <SaveBar onSave={save} saving={saving} testid={`save-${collection}-bottom`} />
      </div>
    </div>
  );
}
